import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import SafeIcon from '../common/SafeIcon'
import ImageWithFallback from './ImageWithFallback'
import { useSite } from '../contexts/SiteContext'
import * as FiIcons from 'react-icons/fi'

const { FiMenu, FiX, FiSettings } = FiIcons

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const location = useLocation()
  const { siteConfig } = useSite()

  const siteName = siteConfig?.site_name || 'ΑΘΛΗΤΙΚΟΣ ΟΜΙΛΟΣ ΧΑΝΙΑ'
  const siteDesc = siteConfig?.site_description || ''
  const logoUrl = siteConfig?.logo_url || ''
  const primaryColor = siteConfig?.primary_color || '#2563eb'

  useEffect(() => {
    setIsMenuOpen(false)
  }, [location.pathname])

  const navigation = useMemo(
    () => [
      { name: 'Αρχική', href: '/' },
      { name: 'Ομάδα', href: '/team' },
      { name: 'Αγωνιστική δραστηριότητα', href: '/matches' },
      { name: 'Ακαδημία', href: '/academy' },
      { name: 'Νέα', href: '/news' },
      { name: 'Media', href: '/media' },
      { name: 'Όροι Ομάδας', href: '/oroiomadas' },
      { name: 'Εγγραφή Ακαδημίας', href: '/academy-registration' },
      { name: 'Επικοινωνία', href: '/contact' },
    ],
    []
  )

  const isActive = (href) => location.pathname === href

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-3 sm:py-4 gap-3">
          <Link to="/" className="flex items-center gap-3 min-w-0">
            <ImageWithFallback
              src={logoUrl}
              alt={siteName}
              className="w-11 h-11 sm:w-12 sm:h-12 rounded-full object-contain shrink-0"
              fallback={
                <div className="w-11 h-11 sm:w-12 sm:h-12 bg-primary-600 rounded-full flex items-center justify-center shrink-0">
                  <span className="text-white font-bold text-xl">ΑΟΧ</span>
                </div>
              }
            />

            <div className="hidden sm:block min-w-0">
              <h1 className="text-base sm:text-lg font-bold text-primary-800 truncate">
                {siteName}
              </h1>
              {siteDesc ? (
                <p className="text-xs sm:text-sm text-gray-600 truncate">
                  {siteDesc}
                </p>
              ) : null}
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-2 lg:gap-4 flex-wrap justify-end">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className="px-3 py-2 text-sm font-medium transition-colors duration-200 border-b-2"
                style={{
                  color: isActive(item.href) ? primaryColor : undefined,
                  borderColor: isActive(item.href) ? primaryColor : 'transparent',
                }}
              >
                {item.name}
              </Link>
            ))}

            <Link
              to="/admin/login"
              className="ml-1 px-2 py-2 text-sm font-medium text-gray-500 hover:text-gray-800 transition-colors duration-200"
              title="Διαχείριση"
              aria-label="Διαχείριση"
            >
              <SafeIcon icon={FiSettings} className="w-5 h-5" />
            </Link>
          </nav>

          <button
            onClick={() => setIsMenuOpen((v) => !v)}
            className="md:hidden p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-50"
            aria-label={isMenuOpen ? 'Κλείσιμο μενού' : 'Άνοιγμα μενού'}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-nav"
          >
            <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
          </button>
        </div>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              id="mobile-nav"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden pb-4"
            >
              <nav className="flex flex-col gap-1 pt-2">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150"
                    style={{
                      color: isActive(item.href) ? primaryColor : undefined,
                      backgroundColor: isActive(item.href)
                        ? `${primaryColor}14`
                        : undefined,
                    }}
                  >
                    {item.name}
                  </Link>
                ))}

                <div className="mt-2 pt-2 border-t border-gray-100">
                  <Link
                    to="/admin/login"
                    className="px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-md flex items-center gap-2"
                  >
                    <SafeIcon icon={FiSettings} className="w-4 h-4" />
                    Διαχείριση
                  </Link>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  )
}

export default Header
